import React, { useEffect, useState } from "react";
import styled from "styled-components";

/**
 * This app's one visible toast primitive — every `growl` call site in this app
 * (`customerAppsHostAdapter.ts`'s `notify` and every feature under `features/item-browser`)
 * renders through this, not a console-log stand-in.
 * `@cinesys/ui` has no Toast/Notification/Snackbar of its own, so this is deliberately small: a
 * module-level push queue + one host component, no animation library, no queueing/stacking limit
 * beyond a plain flex column.
 *
 * `pushToast` is called from plain, non-component code (`customerAppsHostAdapter.ts`'s `notify`,
 * mutation `onError`/`onSuccess` handlers, event-handler catch blocks) — a module-level
 * subscriber list rather than React context, since there's no guaranteed component tree above
 * every call site to read a context from.
 */

export type ToastKind = "success" | "error";

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

const AUTO_DISMISS_MS = 5000;

let toasts: ToastItem[] = [];
let nextId = 0;
const listeners = new Set<(items: ToastItem[]) => void>();

function publish(): void {
  listeners.forEach((listener) => listener(toasts));
}

/** Test-only: clears the module-level queue between test cases in the same file (jest keeps one
 *  module instance per test FILE, so this state would otherwise leak across `it` blocks within a
 *  file — never called from real app code). */
export function __resetToastsForTests(): void {
  toasts = [];
  nextId = 0;
  publish();
}

/** Queues a toast; auto-dismisses after `AUTO_DISMISS_MS`. Safe to call before `<ToastHost>` has
 *  mounted — the queue is plain module state, not tied to a subscriber existing yet. */
export function pushToast(message: string, kind: ToastKind): void {
  const item: ToastItem = { id: nextId++, message, kind };
  toasts = [...toasts, item];
  publish();
  window.setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== item.id);
    publish();
  }, AUTO_DISMISS_MS);
}

// Fixed top-right, offset well clear of the item/bulk gear buttons (35px at their largest,
// `Gear.tsx`'s own `$large`) and any page chrome anchored to the top edge, so a toast never sits
// on top of the gear menu's own popup.
const Stack = styled.div`
  position: fixed;
  top: 70px;
  right: 70px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  max-width: 340px;
`;

const ToastBubble = styled.div<{ $kind: ToastKind }>`
  pointer-events: auto;
  padding: 10px 15px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--gray500);
  background-color: ${(p) =>
    p.$kind === "success" ? "var(--greenBackground)" : "var(--redBackground)"};
  border: 1px solid
    ${(p) =>
      p.$kind === "success" ? "var(--greenBorder)" : "var(--redBorder)"};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

/** Mounted once, near the app root (`App.tsx`) — see that file for placement. */
export function ToastHost(): React.ReactElement | null {
  const [items, setItems] = useState<ToastItem[]>(toasts);

  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (!items.length) {
    return null;
  }

  return (
    <Stack>
      {items.map((item) => (
        <ToastBubble
          key={item.id}
          $kind={item.kind}
          data-testid="toast"
          role="status"
        >
          {item.message}
        </ToastBubble>
      ))}
    </Stack>
  );
}

/** Same call shape as Cantemo's jQuery `$.growl(message, kind)` (no jQuery available in this
 *  app), so a call site converts by dropping the `$.` prefix and importing this instead. */
export const growl = (message: string, kind: ToastKind = "success"): void =>
  pushToast(message, kind);
